class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const openDrawer = () => {
      this.prepareToOpen();
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
        document.addEventListener('click', this.handleOutsideClick);
      });
    };

    if (triggeredBy) {
      this.refreshCartContents().finally(openDrawer);
    } else {
      openDrawer();
    }

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.removeEventListener('click', this.handleOutsideClick);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  handleOutsideClick(event) {
    if (!this.classList.contains('active')) return;
    if (event.target.closest('.drawer__inner') || event.target.closest('#cart-icon-bubble')) return;

    this.close();
  }


  refreshCartContents() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceDrawer = html.querySelector('cart-drawer');
        const sourceElement = html.querySelector('.drawer__inner');
        const targetElement = this.querySelector('.drawer__inner');

        if (sourceDrawer) this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
        if (targetElement && sourceElement) {
          targetElement.innerHTML = sourceElement.innerHTML;
        }
      })
      .catch((e) => console.error(e));
  }

  prepareToOpen() {
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.ensurePickupNotice();
  }

  ensurePickupNotice() {
    const cartItems = this.querySelector('#CartDrawer-CartItems');
    if (!cartItems) return;

    const notice = cartItems.querySelector('.cart-pickup-notice');
    const hasPickupOnlyItem = cartItems.querySelector('[data-pickup-only-item="true"]');

    if (!hasPickupOnlyItem) {
      if (notice) notice.remove();
      return;
    }

    if (notice) {
      notice.hidden = false;
      notice.style.display = 'flex';
      return;
    }

    cartItems.insertAdjacentHTML(
      'afterbegin',
      `
        <div class="cart-pickup-notice" role="alert" style="display:flex;align-items:flex-start;gap:12px;padding:14px 18px;margin-bottom:16px;border-radius:6px;background-color:#fff8e1;border:1px solid #f5a623;color:#7a4f00;">
          <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          <div>
            <strong style="display:block;margin-bottom:4px;">In-Store Pickup Required</strong>
            <span class="cart-pickup-notice__text">Your cart contains items that are only available for in-store pickup. Your entire order must be picked up in store.</span>
          </div>
        </div>
      `
    );
  }


  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
